<template>
  <MembershipForm
    ref="membershipFormRef"
    :applicant="applicant"
    :disabled="saving"
    :stipId="stip.id"
    @submit="submit"
  ></MembershipForm>

  <p v-show="error" class="error">
    Your changes could not be saved. Please try again. If the issue persists, please contact our
    support team at
    <a class="underline" :href="`tel:${hrefTFN}`">{{ tollFreeNumber }}</a> or
    <a class="underline" :href="`mailto:${emailAddress}`">{{ emailAddress }}</a
    >.
  </p>
</template>
<script setup lang="ts">
import { useStore } from "vuex";
import { onMounted, ref, computed } from "vue";
import type { Applicant } from "@/models/application";
import type { Stipulation } from "@/models/opal";
import type { UpdateMembershipMemberIdRequest } from "@/models/api";
import { $api } from "@/services/api";
import useApplicationData from "@/composables/useApplicationData";
import MembershipForm from "@/components/checklist/MembershipForm.vue";

const store = useStore();

const membershipFormRef = ref<InstanceType<typeof MembershipForm> | null>(null);

interface Props {
  stip: Stipulation;
  applicant?: Applicant;
}

const props = withDefaults(defineProps<Props>(), {});

const saving = ref<boolean>(false);
const error = ref<boolean>(false);
const loading = ref<boolean>(true);

const emit = defineEmits<{
  (e: "success", updatedStip: Stipulation): void;
}>();

const { tollFreeNumber, hrefTFN, emailAddress } = useApplicationData();

const oidcUser = computed(() => store.getters["oidcStore/oidcUser"]);

const updateMembershipMemberIdRequest = computed<UpdateMembershipMemberIdRequest>(() => {
  return {
    applicantId: props.applicant?.id ?? 0,
    memberId: membershipFormRef.value?.memberId ?? "",
    updatedByUserId: oidcUser.value?.sub,
    stipulationId: props.stip?.id ?? 0,
  };
});

async function submit() {
  error.value = false;
  saving.value = true;

  const valid = membershipFormRef.value?.validateForm();
	
  if (valid === true) {
    const response = await $api.applicant.updateApplicantMembershipMemberId(
      updateMembershipMemberIdRequest.value,
    );

    if (response && !response.error) {
      // updates stip status to pending
      let updatedMembershipStip = props.stip;
      updatedMembershipStip.stipulationStatusId = response.stipulationStatusId;
      updatedMembershipStip.stipulationStatusName = response.stipulationStatusName;

      // updates applicant membership
      store.dispatch("updateApplicantMembership", response.membership);

      emit("success", updatedMembershipStip);
    } else {
      error.value = true;
      saving.value = false;
    }
  } else {
    saving.value = false;
  }
}

onMounted(async () => {
  membershipFormRef.value?.setMembershipInfo();
  loading.value = false;
});
</script>
<style scoped>
.checkmark {
  top: -2px;
}
.underline {
  text-decoration: underline;
}
.error,
.error a {
  color: #8b0e04;
}
</style>
